import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import i18n from "i18n-react";
import DashboardPanel from "../../../components/dashboard/DashboardPanel";
import DashboardRankingModal from "../../../components/dashboard/DashboardRankingModal";
import { formatCurrency, formatNumber, getInitials } from "./ordersDashboardFormatters";

const avatarClasses = [
    "dashboard-avatar-purple",
    "dashboard-avatar-teal",
    "dashboard-avatar-blue",
    "dashboard-avatar-coral",
    "dashboard-avatar-amber"
];
const RANKING_MODAL_PAGE_SIZE = 25;

const getBarWidth = (value, maxValue) => {
    if (!maxValue) return 0;
    return Math.max(8, Math.round((Number(value || 0) * 100) / maxValue));
};

const EmptyRows = () => (
    <div className="dashboard-empty-row">{i18n.translate("there_are_no_results_for_the_search")}</div>
);

const normalizeRankingPage = (result, fallbackPage = 0) => {
    if (Array.isArray(result)) {
        return {
            items: result,
            page: fallbackPage,
            hasNext: false,
            totalItems: result.length
        };
    }

    return {
        items: result?.items || [],
        page: Number(result?.page ?? fallbackPage),
        hasNext: Boolean(result?.hasNext),
        totalItems: Number(result?.totalItems || 0)
    };
};

export const SellerRanking = ({ sellers }) => {
    if (!sellers || sellers.length === 0) return <EmptyRows />;

    const previewSellers = sellers.slice(0, 5);
    const maxSales = Math.max(...previewSellers.map((seller) => Number(seller.totalSales || 0)));

    return previewSellers.map((seller, index) => (
        <div className="dashboard-ranking-row" key={seller.username || index}>
            <div className={`dashboard-avatar ${avatarClasses[index % avatarClasses.length]}`}>
                {getInitials(seller.fullName || seller.username)}
            </div>
            <div className="dashboard-ranking-info">
                <div className="dashboard-ranking-name">{seller.fullName || seller.username}</div>
                <div className="dashboard-ranking-meta">
                    {formatNumber(seller.orderCount)} {i18n.translate("orders_dashboard_orders")}
                    {seller.belowAverage && <span className="dashboard-danger-text"> · {i18n.translate("orders_dashboard_below_average")}</span>}
                </div>
                <div className="dashboard-bar-wrap">
                    <div className="dashboard-bar-fill" style={{ width: `${getBarWidth(seller.totalSales, maxSales)}%` }} />
                </div>
            </div>
            <div className="dashboard-ranking-amount">
                {formatCurrency(seller.totalSales)}
                {index === 0 && <span className="dashboard-badge dashboard-badge-neutral">{seller.rank || 1}°</span>}
            </div>
        </div>
    ));
};

export const ProductRanking = ({ products }) => {
    if (!products || products.length === 0) return <EmptyRows />;

    return products.slice(0, 5).map((product, index) => (
        <div className="dashboard-tabular-row" key={product.productReplicaId || index}>
            <span className="dashboard-tag">{product.productCode || "-"}</span>
            <span className="dashboard-tabular-main">{product.productDescription || "-"}</span>
            <span className="dashboard-tabular-meta">{formatNumber(product.quantity)} u</span>
            <span className="dashboard-tabular-value">{formatCurrency(product.totalRevenue)}</span>
        </div>
    ));
};

export const CustomerRanking = ({ customers }) => {
    if (!customers || customers.length === 0) return <EmptyRows />;

    const previewCustomers = customers.slice(0, 5);
    const maxSales = Math.max(...previewCustomers.map((customer) => Number(customer.totalSales || 0)));

    return previewCustomers.map((customer, index) => {
        const customerName = customer.customerAlias || customer.customerName;

        return (
            <div className="dashboard-ranking-row" key={customer.customerReplicaId || index}>
                <div className={`dashboard-avatar ${avatarClasses[(index + 2) % avatarClasses.length]}`}>
                    {index === 0 ? "★" : getInitials(customerName)}
                </div>
                <div className="dashboard-ranking-info">
                    <div className="dashboard-ranking-name">{customerName || "-"}</div>
                    <div className="dashboard-ranking-meta">
                        {formatNumber(customer.orderCount)} {i18n.translate("orders_dashboard_orders")}
                    </div>
                    <div className="dashboard-bar-wrap">
                        <div className="dashboard-bar-fill" style={{ width: `${getBarWidth(customer.totalSales, maxSales)}%` }} />
                    </div>
                </div>
                <div className="dashboard-ranking-amount">{formatCurrency(customer.totalSales)}</div>
            </div>
        );
    });
};

SellerRanking.propTypes = {
    sellers: PropTypes.arrayOf(PropTypes.shape({
        username: PropTypes.string,
        fullName: PropTypes.string,
        totalSales: PropTypes.number,
        orderCount: PropTypes.number,
        belowAverage: PropTypes.bool,
        rank: PropTypes.number
    }))
};

SellerRanking.defaultProps = {
    sellers: []
};

ProductRanking.propTypes = {
    products: PropTypes.arrayOf(PropTypes.shape({
        productReplicaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        productCode: PropTypes.string,
        productDescription: PropTypes.string,
        quantity: PropTypes.number,
        totalRevenue: PropTypes.number
    }))
};

ProductRanking.defaultProps = {
    products: []
};

CustomerRanking.propTypes = {
    customers: PropTypes.arrayOf(PropTypes.shape({
        customerReplicaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        customerAlias: PropTypes.string,
        customerName: PropTypes.string,
        orderCount: PropTypes.number,
        totalSales: PropTypes.number
    }))
};

CustomerRanking.defaultProps = {
    customers: []
};

const RankingPanel = ({ title, icon, modalItems, modalColumns, modalSearchText: getModalSearchText, loadModalItems, children }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loadedModalItems, setLoadedModalItems] = useState([]);
    const [modalLoading, setModalLoading] = useState(false);
    const [modalLoadingMore, setModalLoadingMore] = useState(false);
    const [modalError, setModalError] = useState("");
    const [modalSearchText, setModalSearchText] = useState("");
    const [modalPage, setModalPage] = useState(0);
    const [modalHasNext, setModalHasNext] = useState(false);
    const searchDebounceRef = useRef(null);
    const lastRequestRef = useRef(0);
    const items = modalItems || [];
    const showAction = items.length > 0;
    const loadModalPage = useCallback(({ page = 0, search = "", append = false }) => {
        if (!loadModalItems) return Promise.resolve();

        const requestId = lastRequestRef.current + 1;
        lastRequestRef.current = requestId;

        if (append) {
            setModalLoadingMore(true);
        } else {
            setModalLoading(true);
            setModalHasNext(false);
        }
        setModalError("");

        return loadModalItems({
            page,
            size: RANKING_MODAL_PAGE_SIZE,
            search
        })
            .then((result) => {
                if (requestId !== lastRequestRef.current) return;

                const normalizedPage = normalizeRankingPage(result, page);
                setLoadedModalItems((prev) => (append ? [...prev, ...normalizedPage.items] : normalizedPage.items));
                setModalPage(normalizedPage.page);
                setModalHasNext(normalizedPage.hasNext);
            })
            .catch((errorResponse) => {
                if (requestId !== lastRequestRef.current) return;

                setModalError(errorResponse.message || i18n.translate("orders_dashboard_load_error"));
            })
            .finally(() => {
                if (requestId !== lastRequestRef.current) return;

                setModalLoading(false);
                setModalLoadingMore(false);
            });
    }, [loadModalItems]);

    const openModal = () => {
        setIsModalOpen(true);
        setLoadedModalItems([]);
        setModalError("");
        setModalSearchText("");
        setModalPage(0);
        setModalHasNext(false);

        if (loadModalItems) {
            loadModalPage({ page: 0, search: "", append: false });
            return;
        }

        setLoadedModalItems(items);
    };
    const closeModal = () => {
        setIsModalOpen(false);
        lastRequestRef.current += 1;
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
    };
    const handleSearchChange = (search) => {
        setModalSearchText(search);
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }

        searchDebounceRef.current = setTimeout(() => {
            setLoadedModalItems([]);
            setModalPage(0);
            loadModalPage({ page: 0, search, append: false });
        }, 350);
    };
    const loadNextPage = () => {
        if (!modalHasNext || modalLoading || modalLoadingMore) return;
        loadModalPage({ page: modalPage + 1, search: modalSearchText, append: true });
    };
    useEffect(() => () => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
    }, []);

    const action = showAction ? (
        <button className="dashboard-card-action" type="button" onClick={openModal}>
            {i18n.translate("default_see_all")}
        </button>
    ) : null;

    return (
        <>
            <DashboardPanel title={title} icon={icon} action={action}>
                {children}
            </DashboardPanel>
            {showAction && (
                <DashboardRankingModal
                    isOpen={isModalOpen}
                    title={title}
                    items={loadedModalItems}
                    columns={modalColumns}
                    searchPlaceholder={i18n.translate("orders_dashboard_search_by_name")}
                    emptyLabel={i18n.translate("there_are_no_results_for_the_search")}
                    loading={modalLoading}
                    loadingLabel={i18n.translate("default_loading")}
                    loadingMore={modalLoadingMore}
                    loadMoreLabel={i18n.translate("default_loading")}
                    errorMessage={modalError}
                    getSearchText={getModalSearchText}
                    hasNext={modalHasNext}
                    onLoadMore={loadNextPage}
                    onSearchChange={loadModalItems ? handleSearchChange : null}
                    onClose={closeModal}
                />
            )}
        </>
    );
};

export const useSellerRankingModal = (sellers) => useMemo(() => ({
    items: (sellers || []).map((seller, index) => ({
        ...seller,
        id: seller.username || index
    })),
    columns: [
        {
            key: "rank",
            label: "#",
            width: "52px",
            render: (seller, index) => seller.rank || index + 1
        },
        {
            key: "fullName",
            label: i18n.translate("default_user"),
            width: "minmax(180px, 1fr)",
            render: (seller) => seller.fullName || seller.username || "-"
        },
        {
            key: "orderCount",
            label: i18n.translate("orders_dashboard_orders"),
            width: "90px",
            align: "right",
            render: (seller) => formatNumber(seller.orderCount)
        },
        {
            key: "totalSales",
            label: i18n.translate("orders_dashboard_total_sales"),
            width: "120px",
            align: "right",
            render: (seller) => formatCurrency(seller.totalSales)
        }
    ],
    getSearchText: (seller) => `${seller.fullName || ""} ${seller.username || ""}`
}), [sellers]);

export const useProductRankingModal = (products) => useMemo(() => ({
    items: (products || []).map((product, index) => ({
        ...product,
        id: product.productReplicaId || product.productCode || index
    })),
    columns: [
        {
            key: "rank",
            label: "#",
            width: "52px",
            render: (product, index) => product.rank || index + 1
        },
        {
            key: "productCode",
            label: i18n.translate("default_code"),
            width: "120px",
            render: (product) => product.productCode || "-"
        },
        {
            key: "productDescription",
            label: i18n.translate("default_description"),
            width: "minmax(220px, 1fr)",
            render: (product) => product.productDescription || "-"
        },
        {
            key: "quantity",
            label: i18n.translate("default_quantity"),
            width: "90px",
            align: "right",
            render: (product) => formatNumber(product.quantity)
        },
        {
            key: "totalRevenue",
            label: i18n.translate("orders_dashboard_total_sales"),
            width: "120px",
            align: "right",
            render: (product) => formatCurrency(product.totalRevenue)
        }
    ],
    getSearchText: (product) => `${product.productCode || ""} ${product.productDescription || ""}`
}), [products]);

export const useCustomerRankingModal = (customers) => useMemo(() => ({
    items: (customers || []).map((customer, index) => ({
        ...customer,
        id: customer.customerReplicaId || index
    })),
    columns: [
        {
            key: "rank",
            label: "#",
            width: "52px",
            render: (customer, index) => customer.rank || index + 1
        },
        {
            key: "customerName",
            label: i18n.translate("default_customer"),
            width: "minmax(220px, 1fr)",
            render: (customer) => customer.customerAlias || customer.customerName || "-"
        },
        {
            key: "orderCount",
            label: i18n.translate("orders_dashboard_orders"),
            width: "90px",
            align: "right",
            render: (customer) => formatNumber(customer.orderCount)
        },
        {
            key: "totalSales",
            label: i18n.translate("orders_dashboard_total_sales"),
            width: "120px",
            align: "right",
            render: (customer) => formatCurrency(customer.totalSales)
        }
    ],
    getSearchText: (customer) => `${customer.customerAlias || ""} ${customer.customerName || ""}`
}), [customers]);

RankingPanel.propTypes = {
    title: PropTypes.string.isRequired,
    icon: PropTypes.node.isRequired,
    modalItems: PropTypes.array,
    modalColumns: PropTypes.array,
    modalSearchText: PropTypes.func,
    loadModalItems: PropTypes.func,
    children: PropTypes.node.isRequired
};

RankingPanel.defaultProps = {
    modalItems: [],
    modalColumns: [],
    modalSearchText: () => "",
    loadModalItems: null
};

export default RankingPanel;
